import {
  Component,
  effect,
  EventEmitter,
  input,
  Input,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import {
  GeneralAttachmentModel,
  MaintananceLevelModel,
  MaintenanceCriteriaModel,
  MaintenanceCriteriaSettingModel,
  PatientProgramModel,
  PatientTargetModel,
  PatientTargetStatus,
  TargetFailOption,
  TargetTemplateModel,
  TargetType,
  AlarmOption,
  PatientPlaylistModel,
  SkillAreaModel,
} from '../../../core/api/models';
import { SupervisorApiService } from '../../../core/api/services';
import { SupervisorHelperService } from '../../supervisor.helper.service';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
import { SupervisorBaseComponent } from '../../supervisor-base.component';
import {
  getPatientTargetStatusLabel,
  getTargetFailOptions,
  getPatientTargetStatusAtAGlanceColor,
  isTargetActive,
  getPatientTargetStatusAtAGlanceText,
  getPatientTargetStatuses,
  getPatientTargetStatusCircleColor,
  getAlarmOptions,
  getPatientTargetStatusAtAGlanceTintColor,
} from '../../../shared/utils/utils';
import { AuthService } from '../../../auth/auth.service';
import { Permissions } from '../../../core/api/models';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { Router } from '@angular/router';
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';
import { forkJoin } from 'rxjs';
import { OverlayPanel } from 'primeng/overlaypanel';

// CKEditor imports
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';
import { AddAttachmentModalComponent } from '../../../shared/components/add-attachment/add-attachment-modal/add-attachment-modal.component';
import { AttachmentViewComponent } from '../../../shared/components/attachment-view/attachment-view.component';
import {
  ChangeLogModalComponent,
} from '../../../shared/components/change-log-modal/change-log-modal.component';
import { SparkLogEntityType } from '../../../core/api/models';
import { SupervisorOrderMode } from '../../supervisor-ordering';

@Component({
  selector: 'app-target',
  templateUrl: './target.component.html',
  styleUrl: './target.component.css',
  standalone: false,
})
export class TargetComponent extends SupervisorBaseComponent implements OnInit {
  patientID = input.required<string>();

  showGraph = signal(false);
  showData = signal(false);

  @Input() program: PatientProgramModel | undefined;
  @Input() target: PatientTargetModel | undefined;
  @Input() isSelecting: boolean = false;
  @Input() selectAll: boolean = false;

  targetSelected: boolean = false;

  @Output() selectTarget = new EventEmitter<number>();

  // Order functionality outputs to parent
  @Output() moveUp = new EventEmitter<number>();
  @Output() moveDown = new EventEmitter<number>();
  @Output() moveToTop = new EventEmitter<number>();
  @Output() moveToBottom = new EventEmitter<number>();

  isCriterionDataExpanded = signal(false);
  isConfigurationDataExpanded = signal(false);
  isLastChangedDatesExpanded = signal(false);
  isInstructionsExpanded = signal(false);

  isEditingTargetNotes = false;

  targetAlerts = signal<any[]>([]);
  targetStatuses: any[] = getPatientTargetStatuses();
  newTargetStatus: number | null = null;
  newTargetInstructions = signal('');
  newInstructions: string = '';

  showStatusEdit: boolean = false;
  showStatusCircleEdit: boolean = false;
  showSessionTrialCountEdit: boolean = false;
  showPlaylistsEdit: boolean = false;
  showInstructionsEdit: boolean = false;
  showProgramsEdit: boolean = false;
  showOpenedDateEdit: boolean = false;
  showSkillsEdit: boolean = false;

  @Input() isIncludeDeletedFilterSelected = false;
  @Input() isIncludeArchivedFilterSelected = false;
  @Input() isIncludeMasteredFilterSelected = false;

  // Order functionality inputs from parent
  @Input() orderMode: SupervisorOrderMode = 'alphabetical';
  @Input() isEditMode: boolean = false;
  public Editor = Editor;

  isLoadingAttachments: boolean = false;
  showAddAttachments: boolean = false;
  doShowAttachmentView: boolean = false;
  addAttachmentView: string = 'patientInstructions';

  @ViewChild('changeLogModal') changeLogModal!: ChangeLogModalComponent;
  @ViewChild('subtargetProgramWarningOp')
  subtargetProgramWarningOp?: OverlayPanel;
  entityTypeTarget = SparkLogEntityType.Target;
  targetID: number = 0;
  readonly subtargetProgramWarningTooltip =
    'This target will not appear for use because it is a sub-target. Click for details.';
  attachmentList: GeneralAttachmentModel[] = [];
  selectedAttachment: GeneralAttachmentModel | null = null;

  possiblePlaylists = signal<PatientPlaylistModel[]>([]);
  selectedPlaylistIDs = signal<number[]>([]);
  possiblePrograms = signal<PatientProgramModel[]>([]);
  selectedProgramIDs = signal<number[]>([]);
  sessionTrialCount = signal<number>(0);
  newOpenedDate = signal<string>('');
  possibleSkills = signal<SkillAreaModel[]>([]);
  selectedSkillID = signal<number>(0);
  initialInstructions: string = '';
  maintenanceSummary = signal<string | null>(null);

  // CKEditor configuration
  public editorConfig = editorConfig;

  private instructionsUpdateTimeout: any;
  private instructionsChangedOnEffect: boolean = false;

  @Input() isFullColorTargets = false;

  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    private router: Router,
    patientDataFacadeService: PatientDataFacadeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );

    effect(() => {
      const instructions = this.newTargetInstructions();
      if (instructions !== this.target?.Instructions && !this.isLoading) {
        this.instructionsChangedOnEffect = true;
        // Debounce the update call to prevent multiple rapid calls
        clearTimeout(this.instructionsUpdateTimeout);
        this.instructionsUpdateTimeout = setTimeout(() => {
          this.updateTargetInstructions(true);
        }, 1000);
      }
    });
  }

  ngOnInit(): void {
    // if target notes is new, show it as an alert
    if (this.target?.IsTargetNoteNew) {
      this.targetAlerts.set([
        {
          id: 1,
          message: this.target?.TargetNotes ?? '',
          sender: this.target?.NotesLastModifiedByUserFullName ?? '',
          type: 'warning',
        },
      ]);
    }
    this.newInstructions = this.target?.Instructions ?? 'None';
    this.newTargetInstructions.set(this.target?.Instructions ?? 'None');
    this.possiblePlaylists.set(
      this.supervisorHelperService.playlists().filter((p) => !p.IsDeleted)
    );
    this.selectedPlaylistIDs.set(this.target?.PatientPlaylistIDs ?? []);
    this.sessionTrialCount.set(this.target?.DesiredTrials ?? 0);
    this.possiblePrograms.set(
      this.supervisorHelperService.programs().filter((p) => !p.IsDeleted)
    );
    this.selectedProgramIDs.set(this.target?.PatientProgramIDs ?? []);
    this.newOpenedDate.set(
      this.target?.DateOpened
        ? new Date(this.target.DateOpened).toISOString().split('T')[0]
        : ''
    );
    this.possibleSkills.set(
      this.supervisorHelperService.skillAreas().filter((s) => !s.IsDeleted)
    );
    this.selectedSkillID.set(this.target?.SkillAreaID ?? 0);
    this.loadMaintenanceSummary();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSelecting']) {
      if (this.isSelecting == false) {
        this.targetSelected = false;
        this.showInstructionsEdit = false;
        this.showStatusEdit = false;
        this.showStatusCircleEdit = false;
      } else {
        this.showInstructionsEdit = false;
        this.showStatusEdit = false;
        this.showStatusCircleEdit = false;
      }
    }
    if (changes['selectAll']) {
      if (this.selectAll == true) {
        this.targetSelected = true;
        this.selectTarget.emit(this.target?.ID ?? 0);
      } else this.targetSelected = false;
    }
    if (changes['target']) {
      this.loadMaintenanceSummary();
    }
  }

  _isExpanded: boolean = false;
  @Input()
  get isExpanded(): boolean {
    return !!this._isExpanded;
  }

  set isExpanded(val: boolean) {
    this._isExpanded = !!val;
    this.isExpandedChange.emit(this._isExpanded); // Emit changes
  }

  @Output() isExpandedChange = new EventEmitter<boolean>();

  toggleCollapsed() {
    if (!this.isSelecting) {
      this.isExpanded = !this.isExpanded;
      this.showStatusCircleEdit = false;
      this.showInstructionsEdit = false;
      console.log(this.target);
    } else {
      this.targetSelected = !this.targetSelected;
      this.selectTarget.emit(this.target?.ID ?? 0);
    }
    if (this.isExpanded) {
      this.loadAttachments();
    }
  }

  showFlag() {
    return this.target?.IsFavorite || this.target?.IsSpecialFavorite;
  }

  getFlagColor() {
    return this.target?.IsFavorite ? 'lightgreen' : 'darkgreen';
  }

  showCircle() {
    return getPatientTargetStatusAtAGlanceText(this.target) !== 'Unknown';
  }

  getCircleColor() {
    return getPatientTargetStatusCircleColor(this.target);
  }

  getCircleTooltip() {
    return getPatientTargetStatusAtAGlanceText(this.target);
  }

  shouldDisplayMasteredDate(): boolean {
    return (
      this.target?.TargetStatus === PatientTargetStatus.Mastered ||
      this.target?.TargetStatus === PatientTargetStatus.Maintenance ||
      this.target?.TargetStatus === PatientTargetStatus.Fluent
    );
  }

  shouldShowMaintenanceSummary(): boolean {
    return (
      this.target?.TargetStatus === PatientTargetStatus.Maintenance &&
      this.maintenanceSummary() != null
    );
  }

  private loadMaintenanceSummary(): void {
    if (
      this.target?.TargetStatus !== PatientTargetStatus.Maintenance ||
      !this.target?.PatientID
    ) {
      this.maintenanceSummary.set(null);
      return;
    }

    forkJoin({
      maintenanceLevels: this.supervisorApiService.GetMaintananceLevels(),
      currentMaintenanceCriteriaFull:
        this.supervisorApiService.GetCurrentMaintenanceCriteriaFull({
          patientID: this.target.PatientID,
        }),
      patientMaintenanceCriteriaFull:
        this.supervisorApiService.GetMaintenanceCriteriaFull({
          patientID: this.target.PatientID,
        }),
    }).subscribe({
      next: ({
        maintenanceLevels,
        currentMaintenanceCriteriaFull,
        patientMaintenanceCriteriaFull,
      }) => {
        const effectiveCriteriaModels =
          currentMaintenanceCriteriaFull.MaintenanceCriterias?.filter(
            (criteria) =>
              criteria.MaintenanceLevelID != null && criteria.IsActive !== false
          ) ?? [];
        const patientSpecificCriteriaModels =
          patientMaintenanceCriteriaFull.MaintenanceCriterias?.filter(
            (criteria) =>
              criteria.MaintenanceLevelID != null && criteria.IsActive !== false
          ) ?? [];
        const hasActivePatientSpecificCriteria = patientSpecificCriteriaModels.some(
          (criteria) => !criteria.IsLegacyMaintenanceCriteria
        );
        const hasLegacyPatientSpecificCriteria = patientSpecificCriteriaModels.some(
          (criteria) => !!criteria.IsLegacyMaintenanceCriteria
        );
        const isUsingLegacyMaintenanceCriteria =
          !hasActivePatientSpecificCriteria && hasLegacyPatientSpecificCriteria;
        const availableCriteriaModels =
          patientSpecificCriteriaModels.length > 0
            ? patientSpecificCriteriaModels.filter((criteria) =>
                isUsingLegacyMaintenanceCriteria
                  ? !!criteria.IsLegacyMaintenanceCriteria
                  : !criteria.IsLegacyMaintenanceCriteria
              )
            : effectiveCriteriaModels;

        const resolvedCriteria =
          this.getResolvedMaintenanceCriteria(
            this.target?.MaintenanceCriteriaID ?? null,
            availableCriteriaModels,
            effectiveCriteriaModels,
            currentMaintenanceCriteriaFull.MaintenanceCriteriaSetting
          );

        if (!resolvedCriteria) {
          this.maintenanceSummary.set(null);
          return;
        }

        const levelName = this.getMaintenanceLevelName(
          resolvedCriteria.MaintenanceLevelID,
          maintenanceLevels
        );
        const sourceLabel = resolvedCriteria.IsLegacyMaintenanceCriteria
          ? 'Legacy maintenance criteria'
          : resolvedCriteria.PatientID != null
            ? 'Patient-specific maintenance criteria'
            : 'General maintenance criteria';

        this.maintenanceSummary.set(`${levelName} (${sourceLabel})`);
      },
      error: () => {
        this.maintenanceSummary.set(null);
      },
    });
  }

  private getResolvedMaintenanceCriteria(
    maintenanceCriteriaId: number | null,
    availableCriteriaModels: MaintenanceCriteriaModel[],
    effectiveCriteriaModels: MaintenanceCriteriaModel[],
    maintenanceCriteriaSetting: MaintenanceCriteriaSettingModel | undefined
  ): MaintenanceCriteriaModel | undefined {
    if (maintenanceCriteriaId != null) {
      const directMatch = availableCriteriaModels.find(
        (criteria) => criteria.ID === maintenanceCriteriaId
      );
      if (directMatch) {
        return directMatch;
      }

      const fallbackMatch = effectiveCriteriaModels.find(
        (criteria) => criteria.ID === maintenanceCriteriaId
      );
      if (fallbackMatch?.MaintenanceLevelID != null) {
        return availableCriteriaModels.find(
          (criteria) =>
            criteria.MaintenanceLevelID === fallbackMatch.MaintenanceLevelID
        );
      }
    }

    const inheritedMaintenanceLevelId = this.target?.WasMasteredFromProbe
      ? maintenanceCriteriaSetting?.MaintenanceLevelIDWhenFromProbe
      : maintenanceCriteriaSetting?.MaintenanceLevelIDWhenFromAquisition;

    if (inheritedMaintenanceLevelId == null) {
      return undefined;
    }

    return availableCriteriaModels.find(
      (criteria) => criteria.MaintenanceLevelID === inheritedMaintenanceLevelId
    );
  }

  private getMaintenanceLevelName(
    maintenanceLevelId: number | undefined,
    maintenanceLevels: MaintananceLevelModel[]
  ): string {
    return (
      maintenanceLevels.find((level) => level.ID === maintenanceLevelId)
        ?.MaintenanceLevel ?? 'Unknown'
    );
  }

  shouldDisplaySubtargetsList() {
    var shouldDisplayBasedOnTargetType =
      this.target?.TargetType == TargetType.TaskAnalysis ||
      this.target?.TargetType == TargetType.GroupedFrequency ||
      this.target?.TargetType == TargetType.ConditionalDiscrimination;
    return (
      shouldDisplayBasedOnTargetType &&
      (this.target?.SubTargets?.length ?? 0) > 0
    );
  }

  getSubtargetsList(): PatientTargetModel[] {
    var subtargets = this.target?.SubTargets?.map((subTarget) => {
      const target = this.supervisorHelperService
        .targets()
        .find((t) => t.ID === subTarget.ID);
      if (!target) return undefined;
      return target;
    }).filter((t) => t !== undefined) as PatientTargetModel[];

    // filter in/out subtargets that based on selected filters
    var filteredSubtargets = subtargets;
    filteredSubtargets = filteredSubtargets.filter((t) =>
      this.isIncludeDeletedFilterSelected ? true : !t.IsDeleted
    );
    filteredSubtargets = filteredSubtargets.filter((t) =>
      this.isIncludeArchivedFilterSelected ? true : !t.IsArchived
    );
    filteredSubtargets = filteredSubtargets.filter((t) =>
      this.isIncludeMasteredFilterSelected
        ? true
        : t.TargetStatus !== PatientTargetStatus.Mastered &&
          t.TargetStatus !== PatientTargetStatus.Fluent
    );

    return filteredSubtargets;
  }

  targetTypeToDisplay(targetType: TargetType | undefined): string {
    if (!targetType) {
      return 'none';
    }
    return Utils.patientTargetTypeLabel(targetType);
  }

  editTarget() {
    console.log('editTarget clicked!');
    // Store the current expansion state in the parent component before editing
    this.isExpandedChange.emit(this.isExpanded);
    this.supervisorHelperService.additTargetButtonClicked(this.target ?? null);
  }

  isClosed(): boolean {
    if (
      this.target?.DateClosed != null ||
      this.target?.TargetStatus == PatientTargetStatus.Closed ||
      this.target?.TargetStatus == PatientTargetStatus.Fluent
    )
      return true;

    return false;
  }

  isTargetSelected(): boolean {
    if (this.targetSelected === true && this.isSelecting === true) return true;

    return false;
  }

  get canPlotGraph() {
    return this.target?.TargetType
      ? Utils.canPlotGraph(this.target.TargetType)
      : false;
  }

  get includedPlaylists() {
    return (
      this.supervisorHelperService
        .playlists()
        .filter((playlist) =>
          playlist.Items?.some(
            (item) => item.PatientTargetID === this.target?.ID
          )
        )
        .map((playlist) => playlist.PlaylistName)
        .join(', ') || 'none'
    );
  }

  getParentTarget(): PatientTargetModel | undefined {
    if (!this.target?.IsTargetSubtarget || this.target.ID == null) {
      return undefined;
    }

    return this.supervisorHelperService
      .targets()
      .find((parent) =>
        parent.SubTargets?.some((subTarget) => subTarget.ID === this.target?.ID)
      );
  }

  /** Legacy case: sub-target was directly assigned to a program/playlist. */
  isInvalidProgramSubtarget(): boolean {
    if (!this.target?.IsTargetSubtarget) {
      return false;
    }

    return (
      !!this.program ||
      (this.target.PatientProgramIDs?.length ?? 0) > 0 ||
      (this.target.PatientPlaylistIDs?.length ?? 0) > 0
    );
  }

  toggleSubtargetProgramWarning(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.subtargetProgramWarningOp?.toggle(event);
  }

  editParentTargetFromWarning(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.subtargetProgramWarningOp?.hide();
    const parent = this.getParentTarget();
    if (parent) {
      this.supervisorHelperService.additTargetButtonClicked(parent);
    }
  }

  get includedPrograms() {
    const sourceTarget = this.target?.IsTargetSubtarget
      ? this.getParentTarget()
      : this.target;
    const programs =
      this.supervisorHelperService
        .programs()
        .filter((program) =>
          program.Targets?.some((item) => item.ID === sourceTarget?.ID)
        )
        .map((program) => program.ProgramName)
        .join(', ') || 'none';

    return this.target?.IsTargetSubtarget
      ? `${programs} (from Parent Target)`
      : programs;
  }

  get includedSkillArea() {
    const sourceTarget = this.target?.IsTargetSubtarget
      ? this.getParentTarget()
      : this.target;
    const skillArea =
      this.supervisorHelperService
        .skillAreas()
        .find((skillArea) => skillArea.ID === sourceTarget?.SkillAreaID)
        ?.SkillAreaName ?? 'none';

    return this.target?.IsTargetSubtarget
      ? `${skillArea} (from Parent Target)`
      : skillArea;
  }

  onShowGraphClicked() {
    this.showGraph.update((oldValue) => !oldValue);
  }

  onShowDataClicked() {
    this.showData.update((oldValue) => !oldValue);
  }

  canRestoreTargets() {
    const userInfo = this.authService.userInfo;

    // Check if user has the IsSupervisor permission or SparkApp_Supervisor permission
    if (
      userInfo &&
      userInfo.Permissions?.includes(Permissions.SparkApp_Supervisor)
    ) {
      return true;
    }
    return false;
  }

  restoreTarget() {
    this.supervisorApiService
      .RestorePatientTarget({ patientTargetID: this.target?.ID ?? 0 })
      .subscribe(() => {
        this.alertService.success('Target restored');
        this.supervisorHelperService.triggerUpdateSignal();
      });
  }

  goToStaffManagementReportForTarget() {
    if (!this.target?.ID) {
      return;
    }

    const patientIdNumber = Number(this.patientID());

    this.router.navigate(
      ['/supervisor', patientIdNumber, 'management-report'],
      { queryParams: { targetID: this.target.ID } }
    );
  }

  private static readonly applyToAllSubTargetsOption =
    'Apply to all sub-targets';
  private static readonly detachSubTargetsOption =
    'Detach as individual targets';

  deleteTarget() {
    this.alertService
      .confirm('Delete Target', 'Are you sure you want to delete this target?')
      .subscribe((result) => {
        if (result) {
          if ((this.target?.SubTargets?.length ?? 0) > 0) {
            setTimeout(() => {
              this.alertService
                .chooseOption(
                  'Sub-targets',
                  'This target has sub-targets. Choose whether to delete them with the parent, or detach them so they become individual targets of their own type.',
                  [
                    TargetComponent.applyToAllSubTargetsOption,
                    TargetComponent.detachSubTargetsOption,
                  ]
                )
                .subscribe((option) => {
                  if (option === TargetComponent.applyToAllSubTargetsOption) {
                    this.deleteTargetConfirmed(true);
                  } else if (
                    option === TargetComponent.detachSubTargetsOption
                  ) {
                    this.deleteTargetConfirmed(false);
                  }
                });
            }, 100);
          } else {
            this.deleteTargetConfirmed(false);
          }
        }
      });
  }

  doArchiveTarget() {
    if (!this.isLoading) {
      var subTargets = this.getSubtargetsList();
      if (subTargets && subTargets.length > 0) {
        this.alertService
          .chooseOption(
            'Sub-targets',
            'This target has sub-targets. Choose whether to archive them with the parent, or detach them so they become individual targets of their own type.',
            [
              TargetComponent.applyToAllSubTargetsOption,
              TargetComponent.detachSubTargetsOption,
            ]
          )
          .subscribe((option) => {
            if (option === TargetComponent.applyToAllSubTargetsOption) {
              this.archiveTarget(true);
            } else if (option === TargetComponent.detachSubTargetsOption) {
              this.archiveTarget(false);
            }
          });
      } else {
        this.archiveTarget(false);
      }
    }
  }

  archiveTarget(doArchiveSubtargets: boolean) {
    this.supervisorApiService
      .ArchiveTarget({
        targetID: this.target?.ID ?? 0,
        doArchiveSubtargets: doArchiveSubtargets,
      })
      .subscribe(() => {
        this.alertService.success('Target Status Updated');
        this.supervisorHelperService.triggerUpdateSignal();
      });
  }

  updateTargetStatus(): void {
    console.log('update target status pressed');
    if (!this.target?.ID || !this.newTargetStatus) {
      this.alertService.error('Select a target status before updating');
      return;
    }

    const statusID = this.newTargetStatus;
    this.showStatusEdit = false;
    this.showStatusCircleEdit = false;
    this.supervisorApiService
      .UpdateTargetStatus({
        targetID: this.target.ID,
        statusID: statusID,
      })
      .subscribe(() => {
        this.alertService.success('Target Status Updated');
        this.supervisorHelperService.triggerUpdateSignal();
      });
  }

  onInstructionsChange(event: any): void {
    if (event.editor) {
      const data = event.editor.getData();
      this.newTargetInstructions.set(data);
    }
  }

  updateTargetInstructions(silently: boolean = false): void {
    console.log('update target status pressed');
    this.showStatusEdit = false;
    this.supervisorApiService
      .UpdateTargetInstructions({
        targetID: this.target?.ID ?? 0,
        body: {
          ID: null,
          Instructions: this.newTargetInstructions(),
        },
      })
      .subscribe(() => {
        if (!silently) {
          this.alertService.success('Target Instructions Updated');
          this.supervisorHelperService.triggerUpdateSignal();
        }
      });
  }

  updateTargetSessionTrialCount() {
    console.log('update target session trial count pressed');
    this.showSessionTrialCountEdit = false;
    this.isLoading = true;
    this.target!.DesiredTrials = this.sessionTrialCount();
    this.supervisorApiService
      .SavePatientTarget({
        body: {
          PatientTarget: this.target!,
          PropagateArchieve: false,
          PropagateStatus: false,
        },
      })
      .subscribe(() => {
        this.alertService.success('Target Session Trial Count Updated');
        this.supervisorHelperService.triggerUpdateSignal();
        this.isLoading = false;
      });
  }

  updateTargetPlaylists() {
    console.log('update target playlists pressed');
    this.showPlaylistsEdit = false;
    this.isLoading = true;
    this.target!.PatientPlaylistIDs = this.selectedPlaylistIDs();
    this.supervisorApiService
      .SavePatientTarget({
        body: {
          PatientTarget: this.target!,
          PropagateArchieve: false,
          PropagateStatus: false,
        },
      })
      .subscribe(() => {
        this.alertService.success('Target Playlists Updated');
        this.supervisorHelperService.triggerUpdateSignal();
        this.isLoading = false;
      });
  }

  toggleProgramsEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showStatusEdit = false;
    this.showStatusCircleEdit = false;
    this.showSessionTrialCountEdit = false;
    this.showPlaylistsEdit = false;
    this.showInstructionsEdit = false;
    this.showOpenedDateEdit = false;
    this.showSkillsEdit = false;
    this.showProgramsEdit = !this.showProgramsEdit;
  }

  updateTargetPrograms() {
    console.log('update target programs pressed');
    this.showProgramsEdit = false;
    this.isLoading = true;
    this.target!.PatientProgramIDs = this.selectedProgramIDs();
    this.supervisorApiService
      .SavePatientTarget({
        body: {
          PatientTarget: this.target!,
          PropagateArchieve: false,
          PropagateStatus: false,
        },
      })
      .subscribe(() => {
        this.alertService.success('Target Programs Updated');
        this.supervisorHelperService.triggerUpdateSignal();
        this.isLoading = false;
      });
  }

  toggleOpenedDateEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showStatusEdit = false;
    this.showStatusCircleEdit = false;
    this.showSessionTrialCountEdit = false;
    this.showPlaylistsEdit = false;
    this.showInstructionsEdit = false;
    this.showProgramsEdit = false;
    this.showSkillsEdit = false;
    this.showOpenedDateEdit = !this.showOpenedDateEdit;
  }

  updateTargetOpenedDate() {
    console.log('update target opened date pressed');
    this.showOpenedDateEdit = false;
    this.isLoading = true;
    this.target!.DateOpened = this.newOpenedDate();
    this.supervisorApiService
      .SavePatientTarget({
        body: {
          PatientTarget: this.target!,
          PropagateArchieve: false,
          PropagateStatus: false,
        },
      })
      .subscribe(() => {
        this.alertService.success('Target Opened Date Updated');
        this.supervisorHelperService.triggerUpdateSignal();
        this.isLoading = false;
      });
  }

  toggleSkillsEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showSkillsEdit = !this.showSkillsEdit;
    this.showStatusEdit = false;
    this.showStatusCircleEdit = false;
    this.showSessionTrialCountEdit = false;
    this.showPlaylistsEdit = false;
    this.showInstructionsEdit = false;
    this.showProgramsEdit = false;
    this.showOpenedDateEdit = false;
  }

  updateTargetSkills() {
    console.log('update target skills pressed');
    this.showSkillsEdit = false;
    this.isLoading = true;
    this.target!.SkillAreaID = this.selectedSkillID();
    this.supervisorApiService
      .SavePatientTarget({
        body: {
          PatientTarget: this.target!,
          PropagateArchieve: false,
          PropagateStatus: false,
        },
      })
      .subscribe(() => {
        this.alertService.success('Target Skills Updated');
        this.supervisorHelperService.triggerUpdateSignal();
        this.isLoading = false;
      });
  }

  updateTargetFavorite() {
    console.log('update target favorite pressed');
    this.isLoading = true;
    this.supervisorApiService
      .SavePatientTarget({
        body: {
          PatientTarget: this.target!,
          PropagateArchieve: false,
          PropagateStatus: false,
        },
      })
      .subscribe(() => {
        this.alertService.success('Target Favorite Updated');
        this.supervisorHelperService.triggerUpdateSignal();
        this.isLoading = false;
      });
  }

  updateTargetSpecialFavorite() {
    console.log('update target special favorite pressed');
    this.isLoading = true;
    this.supervisorApiService
      .SavePatientTarget({
        body: {
          PatientTarget: this.target!,
          PropagateArchieve: false,
          PropagateStatus: false,
        },
      })
      .subscribe(() => {
        this.alertService.success('Target Special Favorite Updated');
        this.supervisorHelperService.triggerUpdateSignal();
        this.isLoading = false;
      });
  }

  toggleStatusEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showStatusEdit = !this.showStatusEdit;
    this.showStatusCircleEdit = false;
    this.showSessionTrialCountEdit = false;
    this.showPlaylistsEdit = false;
    this.showInstructionsEdit = false;
    this.showProgramsEdit = false;
    this.showOpenedDateEdit = false;
    this.showSkillsEdit = false;
  }

  toggleSessionTrialCountEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showSessionTrialCountEdit = !this.showSessionTrialCountEdit;
    this.showStatusEdit = false;
    this.showStatusCircleEdit = false;
    this.showPlaylistsEdit = false;
    this.showInstructionsEdit = false;
    this.showProgramsEdit = false;
    this.showOpenedDateEdit = false;
    this.showSkillsEdit = false;
  }

  togglePlaylistsEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showPlaylistsEdit = !this.showPlaylistsEdit;
    this.showStatusEdit = false;
    this.showSessionTrialCountEdit = false;
    this.showStatusCircleEdit = false;
    this.showInstructionsEdit = false;
    this.showProgramsEdit = false;
    this.showOpenedDateEdit = false;
    this.showSkillsEdit = false;
  }

  closeStatusCircleEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showStatusCircleEdit = false;
  }

  toggleStatusCircleEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showStatusEdit = false;
    this.showInstructionsEdit = false;
    this.showStatusCircleEdit = !this.showStatusCircleEdit;
    this.showSessionTrialCountEdit = false;
    this.showPlaylistsEdit = false;
    this.showProgramsEdit = false;
    this.showOpenedDateEdit = false;
    this.showSkillsEdit = false;
    e.stopPropagation();
  }

  closeInstructionsEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    if (this.instructionsChangedOnEffect) {
      this.instructionsChangedOnEffect = false;
      this.supervisorHelperService.triggerUpdateSignal();
    }

    this.showInstructionsEdit = false;
  }
  toggleInstructionEdit(e: any) {
    if (this.isSelecting == true) {
      return;
    }
    this.showStatusEdit = false;
    this.showStatusCircleEdit = false;
    this.showInstructionsEdit = !this.showInstructionsEdit;

    e.stopPropagation();
  }
  deleteTargetConfirmed(propagate: boolean = false) {
    console.log('deleteTargetConfirmed!');
    if (this.target?.ID != null) {
      this.supervisorApiService
        .MarkPatientTargetAsDeleted({
          patientTargetID: this.target.ID,
          propagate: propagate,
        })
        .subscribe(
          () => {
            this.alertService.success('Target marked as deleted');
            this.supervisorHelperService.triggerUpdateSignal();
          },
          (error) => {
            console.error(error);
            this.alertService.error(
              Utils.getErrorMessageFromErrorResponse(error)
            );
          }
        );
    }
  }

  getGoalName(goalId: number | undefined | null): string {
    if (!goalId) {
      return 'none';
    }
    return (
      this.supervisorHelperService
        .treatmentGoals()
        .find((goal) => goal.ID === goalId)?.Objective ?? 'none'
    );
  }

  getStatusName(status: PatientTargetStatus | undefined): string {
    if (!status) {
      return 'none';
    }
    return getPatientTargetStatusLabel(status);
  }

  getPromptCodes(): string {
    return this.target?.PromptCodes?.map((pc) => pc.Text).join(', ') || 'none';
  }

  targetFailOptionToDisplay(
    targetFailOption: TargetFailOption | undefined
  ): string {
    if (!targetFailOption) {
      return 'none';
    }
    return (
      getTargetFailOptions().find((tfo) => tfo.value === targetFailOption)
        ?.text || 'none'
    );
  }

  startEditingTargetNotes() {
    this.isEditingTargetNotes = true;
  }

  cancelEditingTargetNotes() {
    this.isEditingTargetNotes = false;
  }

  saveTargetNotes() {
    this.staffApiService
      .ChangeTargetNotes({
        targetId: this.target?.ID ?? 0,
        newNotes: this.target?.TargetNotes ?? '',
        shouldMarkAsNew: false,
      })
      .subscribe(() => {
        this.isEditingTargetNotes = false;
        this.supervisorHelperService.triggerUpdateSignal();
      });
  }

  duplicateTarget() {
    this.alertService
      .confirm(
        'Duplicate Target',
        'Are you sure you want to duplicate this target?'
      )
      .subscribe((result) => {
        if (result) {
          this.isLoading = true;
          this.supervisorApiService
            .DuplicatePatientTarget({
              patientTargetID: this.target?.ID ?? 0,
            })
            .subscribe(
              (savedItem) => {
                this.linkDuplicatedTargetToProgram(savedItem);
              },
              (err) => {
                this.isLoading = false;
                this.alertService.error(
                  Utils.getErrorMessageFromErrorResponse(err)
                );
              }
            );
        }
      });
  }

  private linkDuplicatedTargetToProgram(savedItem: PatientTargetModel) {
    if (!this.program?.ID) {
      this.finishDuplicateTarget();
      return;
    }

    const currentTargets = this.program.Targets ?? [];
    const updatedTargets = currentTargets.some(
      (target) => target.ID === savedItem.ID
    )
      ? currentTargets
      : [...currentTargets, savedItem];

    this.supervisorApiService
      .SavePatientProgram({
        body: {
          PatientProgram: {
            ...this.program,
            Targets: updatedTargets,
          },
          PropagateArchieve: false,
          PropagateStatus: false,
        },
      })
      .subscribe(
        () => {
          this.finishDuplicateTarget();
        },
        (err) => {
          this.isLoading = false;
          this.alertService.error(
            'Target duplicated, but it could not be added to this program. ' +
              Utils.getErrorMessageFromErrorResponse(err)
          );
        }
      );
  }

  private finishDuplicateTarget() {
    this.isLoading = false;
    this.alertService.success('Target duplicated successfully');
    this.supervisorHelperService.triggerUpdateSignal();
  }

  targetNameForDisplay(): string {
    if (!this.target?.TargetName) {
      return '';
    }
    return this.target?.TargetName?.length > 40
      ? this.target?.TargetName?.slice(0, 40) +
          '\n' +
          this.target?.TargetName?.slice(40)
      : this.target?.TargetName;
  }

  dismissAlertButtonClicked(alert: any) {
    console.log('dismissAlertButtonClicked clicked!');
    this.supervisorApiService
      .MarkTargetNoteAsRead({
        targetId: this.target?.ID ?? 0,
      })
      .subscribe(() => {
        this.targetAlerts.set([]);
      });
  }

  lastModifiedByText(): string {
    if (
      !this.target?.NotesLastModifiedByUserFullName ||
      !this.target?.NotesLastModifiedDate
    ) {
      return '';
    }
    return (
      'Last modified by: ' +
      this.target.NotesLastModifiedByUserFullName +
      ' at '
    );
  }

  // Attachment functions

  toggleShowAddAttachments(): void {
    this.showAddAttachments = !this.showAddAttachments;
  }

  openAttachmentView(att: GeneralAttachmentModel): void {
    this.doShowAttachmentView = true;
    this.selectedAttachment = att;
  }

  getImageUrl(att: GeneralAttachmentModel): string {
    var url = 'data:' + att.FileType + ';base64,' + att.ImageThumbnail;
    return url;
  }

  closeAddAttachments() {
    this.showAddAttachments = false;
    this.loadAttachments();
  }

  loadAttachments() {
    this.isLoadingAttachments = true;
    this.supervisorApiService
      .GetTargetAttachments({
        targetID: this.target?.ID ?? 0,
      })
      .subscribe({
        next: (results) => {
          this.attachmentList = results;
          this.isLoadingAttachments = false;
        },
        error: (error) => {
          console.error('Error loading patient instruction attachments', error);
          this.alertService.error(
            'Failed to load patient instruction attachments'
          );
          this.isLoadingAttachments = false;
        },
      });
  }

  closeAttachmentView() {
    this.doShowAttachmentView = false;
    this.selectedAttachment = null;
    this.loadAttachments();
  }

  requiresDuration() {
    return (
      this.target?.TargetType === TargetType.FluencyRate ||
      this.target?.TargetType === TargetType.Rate ||
      this.target?.TargetType === TargetType.WholePartialInterval ||
      this.target?.TargetType === TargetType.Toileting ||
      this.target?.TargetType === TargetType.Duration
    );
  }

  getDurationInMinutes() {
    return this.target?.Duration ? this.target.Duration / 60 : 0;
  }

  shouldShowAlarmOptions() {
    const isRightType =
      this.target?.TargetType === TargetType.Duration ||
      this.target?.TargetType === TargetType.FluencyRate ||
      this.target?.TargetType === TargetType.Rate ||
      this.target?.TargetType === TargetType.Toileting ||
      this.target?.TargetType === TargetType.WholePartialInterval;
    const hasOptionOtherThanNo =
      this.target?.AlarmOption != null &&
      this.target.AlarmOption !== AlarmOption.NoAlarm;
    return isRightType && hasOptionOtherThanNo;
  }

  getAlarmOption() {
    return this.target?.AlarmOption ?? AlarmOption.NoAlarm;
  }

  getAlarmSound() {
    return 'option ' + (this.target?.AlarmSoundOption ?? 1);
  }

  isCurrency(): boolean {
    return this.target?.TargetType === TargetType.Currency;
  }

  shouldDisplayIntensityScale() {
    return this.target?.TargetType === TargetType.Intensity;
  }

  intensityScaleDisplay() {
    return Utils.intensityScaleLabel(
      this.target?.IntensityScaleMin,
      this.target?.IntensityScaleMax,
      this.target?.IntensityScaleValues
    );
  }

  playAlarmSound() {
    const selectedSoundToPlay = this.target?.AlarmSoundOption ?? 1;
    const audio = new Audio(`assets/sounds/alarm-${selectedSoundToPlay}.mp3`);
    audio.play();
    setTimeout(() => {
      audio.pause();
    }, 5000);
  }

  getLabelForAlarmOption(alarmOption: AlarmOption) {
    return (
      getAlarmOptions().find((a) => a.value === alarmOption)?.label ?? 'none'
    );
  }

  getBackgroundColor() {
    if (!this.isFullColorTargets) {
      return '#719eb6';
    }
    return getPatientTargetStatusAtAGlanceColor(this.target);
  }

  getTintColor() {
    if (!this.isFullColorTargets) {
      return '#fff';
    }
    return getPatientTargetStatusAtAGlanceTintColor(this.target);
  }

  // Order control methods
  onMoveUp() {
    if (this.target?.ID) {
      this.moveUp.emit(this.target.ID);
    }
  }

  onMoveDown() {
    if (this.target?.ID) {
      this.moveDown.emit(this.target.ID);
    }
  }

  onMoveToTop() {
    if (this.target?.ID) {
      this.moveToTop.emit(this.target.ID);
    }
  }

  onMoveToBottom() {
    if (this.target?.ID) {
      this.moveToBottom.emit(this.target.ID);
    }
  }

  showChangeLog(): void {
    if (!this.changeLogModal) return;
    this.changeLogModal.entityID = this.target?.ID ?? null;
    this.changeLogModal.entityType = this.entityTypeTarget;
    this.changeLogModal.entityName = this.target?.TargetName ?? '';
    this.changeLogModal.open();
  }
}
